/**
 * @file Custom Print Transaction Details Component.
 */

import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import useClipboard from 'react-use-clipboard';
import ReactTooltip from 'react-tooltip';

import {
  TRANSACTION_ITEM_TYPE,
  CUSTOM_PRINT_ITEM_TYPE,
} from '../../../../constants/transactions';
import {
  getBasenameFromEntity,
  getProductBasenames,
} from '../../../../util/role-definitions';
import { formatPriceForDisplay } from '../../../../util/format-money';
import {
  getCustomPrintItemTypeLabel,
  getTransactionItemRefundedTotal,
} from '../../../../util/transactions';
import { DATE_FORMAT_WITH_TIME } from '../../../../constants/dates';
import Button from '../../../global/Button';
import InfoCircleIcon from '../../../icons/InfoCircleIcon';
import { CURRENCY_SYMBOLS } from '../../../../constants/currency';

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string,
    print_partners: PropTypes.shape({
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          print_partner: PropTypes.shape({
            id: PropTypes.string,
          }),
          print_partner_account_id: PropTypes.string,
        }),
      ),
    }),
  }),
  transaction: PropTypes.shape({
    id: PropTypes.string,
    transaction: PropTypes.shape({
      id: PropTypes.string,
      uuid: PropTypes.string,
      roes_order_id: PropTypes.string,
    }),
    price: PropTypes.shape({
      value: PropTypes.number,
    }),
    item_type: PropTypes.oneOf(Object.keys(TRANSACTION_ITEM_TYPE)),
    custom_print_item_type: PropTypes.oneOf(Object.keys(CUSTOM_PRINT_ITEM_TYPE))
      .isRequired,
    album: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    album_folder: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    identity: PropTypes.shape({
      id: PropTypes.string,
    }),
    vendor_state_tax_price: PropTypes.shape({
      value: PropTypes.number,
    }),
    vendor_local_tax_price: PropTypes.shape({
      value: PropTypes.number,
    }),
    tax_total_price: PropTypes.shape({
      value: PropTypes.number,
    }),
  }),
  basename: PropTypes.oneOf(getProductBasenames()),
};

const CustomPrintTransactionDetails = ({
  account,
  transactionItem,
  basename,
}) => {
  const {
    album_folder: albumFolder,
    prepaid_transaction_item: prepaidTransactionItem,
    purchased_custom_print: purchasedCustomPrint,
    is_prepaid: isPrepaid,
    isConfigured,
  } = transactionItem;
  const {
    uuid: orderNumber,
    order_number: orderShorthand,
    roes_order_id: roesOrderNumber,
    print_partner: printPartner,
    account_address: address,
  } = transactionItem?.transaction ?? {};
  const labId = printPartner
    ? account?.print_partners?.records?.find(
        partner => partner.print_partner.id === printPartner.id,
      )?.print_partner_account_id
    : undefined;

  const album = useMemo(() => {
    if (
      purchasedCustomPrint?.photos?.records?.[0]?.photo?.albums?.records
        ?.length > 0
    ) {
      return purchasedCustomPrint?.photos?.records?.[0]?.photo?.albums
        ?.records?.[0];
    }

    return purchasedCustomPrint?.album;
  }, [purchasedCustomPrint]);

  const [orderNumberCopied, setOrderNumberCopied] = useClipboard(orderNumber);
  const [orderShorthandCopied, setOrderShorthandCopied] =
    useClipboard(orderShorthand);
  const [roesOrderNumberCopied, setRoesOrderNumberCopied] =
    useClipboard(roesOrderNumber);

  const printProductConfiguration = (
    transactionItem?.print_product_configurations?.records ?? []
  ).find(config => !!config?.front_print_file_url);

  return (
    <div>
      <table className="table table-striped mb-3">
        <tbody>
          <tr>
            <td>
              {' '}
              Order Number{' '}
              <Button btnStyle="link" onClick={setOrderNumberCopied}>
                {orderNumberCopied ? 'Copied!' : 'Copy'}
              </Button>
            </td>
            <td>{orderNumber}</td>
          </tr>
          {orderShorthand && (
            <tr>
              <td>
                Order Shorthand{' '}
                <Button btnStyle="link" onClick={setOrderShorthandCopied}>
                  {orderShorthandCopied ? 'Copied!' : 'Copy'}
                </Button>
              </td>
              <td>{orderShorthand}</td>
            </tr>
          )}
          {roesOrderNumber && (
            <tr>
              <td>
                ROES Order Number{' '}
                <Button btnStyle="link" onClick={setRoesOrderNumberCopied}>
                  {roesOrderNumberCopied ? 'Copied!' : 'Copy'}
                </Button>
              </td>
              <td>{roesOrderNumber}</td>
            </tr>
          )}
          {printPartner && (
            <tr>
              <td>Print Partner</td>
              <td>
                {printPartner.name}
                {labId ? ` (Lab ID: ${labId})` : ''}
              </td>
            </tr>
          )}
          <tr>
            <td>Transaction Type</td>
            <td>Custom Print</td>
          </tr>
          <tr>
            <td> Date </td>
            <td>
              {moment(transactionItem.created_at).format(DATE_FORMAT_WITH_TIME)}
            </td>
          </tr>
          <tr>
            <td> Item Type </td>
            <td>
              {getCustomPrintItemTypeLabel(
                transactionItem.custom_print_item_type,
                transactionItem.custom_print?.subtype,
              )}
            </td>
          </tr>
          {transactionItem?.custom_print_name && (
            <tr>
              <td> Product Name </td>
              <td>{transactionItem.custom_print_name}</td>
            </tr>
          )}
          <tr>
            {transactionItem?.custom_print_item_type ===
            CUSTOM_PRINT_ITEM_TYPE.ALBUM ? (
              <Fragment>
                <td> Album </td>
                {album?.id ? (
                  <td>
                    <a
                      href={`/app/${
                        getBasenameFromEntity(album) ?? basename
                      }/albums/${album.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {album.name}
                    </a>
                  </td>
                ) : (
                  <td>{transactionItem?.album_name}</td>
                )}
              </Fragment>
            ) : (
              <Fragment>
                <td> Gallery </td>
                {albumFolder?.id ? (
                  <td>
                    <a
                      href={`/app/${
                        getBasenameFromEntity(albumFolder) ?? basename
                      }/folders/${albumFolder.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {albumFolder.name}
                    </a>
                  </td>
                ) : (
                  <td>{transactionItem?.album_folder_name}</td>
                )}
              </Fragment>
            )}
          </tr>
          {purchasedCustomPrint?.print_file_url && (
            <tr>
              <td>Print File URL</td>
              <td>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={purchasedCustomPrint.print_file_url}
                >
                  {' '}
                  Open in new tab{' '}
                </a>
              </td>
            </tr>
          )}
          {printProductConfiguration && (
            <tr>
              <td>Print Product Configuration</td>
              <td>
                {printProductConfiguration.print_product_configuration?.name}
              </td>
            </tr>
          )}
          <tr>
            <td>{isConfigured ? 'Configured/Sent to Lab' : 'Is Pre-paid'}</td>
            <td>
              {isPrepaid || isConfigured ? (
                <span className="text-extra-bold text-primary">Yes</span>
              ) : (
                'No'
              )}
            </td>
          </tr>
          {address && (
            <tr>
              <td>Shipping Address</td>
              <td>
                <p className="text-light mb-0">{address.name}</p>
                <p className="text-light mb-0">{address.address_line_1}</p>
                <p className="text-light mb-0">{address.address_line_2}</p>
                <p className="text-light mb-0">
                  {[address.city, address.state, address.post_code].join(', ')}
                </p>
                <p className="text-light mb-0">{address.country?.name}</p>
              </td>
            </tr>
          )}
          <tr>
            <td> Identity </td>
            <td>{transactionItem?.identity_name}</td>
          </tr>
          <tr>
            <td> Amount Paid </td>
            <td>
              {(transactionItem?.total_price?.value ?? 0) >= 0 ? (
                <Fragment>
                  {
                    CURRENCY_SYMBOLS[
                      transactionItem?.total_price?.currency ?? 'USD'
                    ]
                  }
                  {formatPriceForDisplay(
                    transactionItem?.total_price?.value ?? 0,
                  )}
                </Fragment>
              ) : (
                'N/A'
              )}
            </td>
          </tr>
          {(transactionItem?.vendor_state_tax_price?.value ?? 0) > 0 && (
            <tr>
              <td> State Tax Collected </td>
              <td>
                {
                  CURRENCY_SYMBOLS[
                    transactionItem?.vendor_state_tax_price?.currency ?? 'USD'
                  ]
                }
                {formatPriceForDisplay(
                  transactionItem?.vendor_state_tax_price?.value ?? 0,
                )}
              </td>
            </tr>
          )}
          {(transactionItem?.vendor_local_tax_price?.value ?? 0) > 0 && (
            <tr>
              <td> Local Tax Collected </td>
              <td>
                {
                  CURRENCY_SYMBOLS[
                    transactionItem?.vendor_local_tax_price?.currency ?? 'USD'
                  ]
                }
                {formatPriceForDisplay(
                  transactionItem?.vendor_local_tax_price?.value ?? 0,
                )}
              </td>
            </tr>
          )}
          {(transactionItem?.tax_total_price?.value ?? 0) > 0 && (
            <tr>
              <td> Total Tax Collected </td>
              <td>
                {
                  CURRENCY_SYMBOLS[
                    transactionItem?.tax_total_price?.currency ?? 'USD'
                  ]
                }
                {formatPriceForDisplay(
                  transactionItem?.tax_total_price?.value ?? 0,
                )}
              </td>
            </tr>
          )}
          <tr>
            <td> Amount Refunded </td>
            <td>
              {
                CURRENCY_SYMBOLS[
                  transactionItem?.total_price?.currency ?? 'USD'
                ]
              }
              {formatPriceForDisplay(
                getTransactionItemRefundedTotal(transactionItem),
              )}
            </td>
          </tr>
          {prepaidTransactionItem && (
            <tr>
              <td>
                <div className="d-flex justify-content-center align-items-center">
                  Pre-paid Amount{' '}
                  <div
                    className="ml-1"
                    data-for="tooltip-prepaid-amount-tip"
                    data-tip=""
                    data-place="top"
                  >
                    {' '}
                    <InfoCircleIcon size={12} />
                    <ReactTooltip
                      id="tooltip-prepaid-amount-tip"
                      className="tooltip"
                    >
                      Pre-paid amount represents the original <br />
                      amount paid for this pre-paid item. Your <br />
                      customer is not charged again for this <br />
                      configuration transaction.
                    </ReactTooltip>
                  </div>
                </div>
              </td>
              <td>
                {(prepaidTransactionItem?.total_price?.value ?? 0) >= 0 ? (
                  <Fragment>
                    {
                      CURRENCY_SYMBOLS[
                        transactionItem?.total_price?.currency ?? 'USD'
                      ]
                    }
                    {formatPriceForDisplay(
                      (prepaidTransactionItem?.total_price?.value ?? 0) -
                        getTransactionItemRefundedTotal(prepaidTransactionItem),
                    )}
                  </Fragment>
                ) : (
                  'N/A'
                )}
              </td>
            </tr>
          )}
          <tr>
            <td>Final Amount</td>
            <td>
              {(transactionItem?.total_price?.value ?? 0) >= 0 ? (
                <Fragment>
                  {
                    CURRENCY_SYMBOLS[
                      transactionItem?.total_price?.currency ?? 'USD'
                    ]
                  }
                  {formatPriceForDisplay(
                    (transactionItem?.total_price?.value ?? 0) -
                      getTransactionItemRefundedTotal(transactionItem),
                  )}
                </Fragment>
              ) : (
                'N/A'
              )}
            </td>
          </tr>
          <tr>
            <td>Discount Purchased</td>
            <td>{transactionItem?.discount_purchased ? 'Yes' : 'No'}</td>
          </tr>
          <tr>
            <td>Shipping Purchased</td>
            <td>{transactionItem?.shipping_purchased ? 'Yes' : 'No'}</td>
          </tr>
          <tr>
            <td>Number of Photos</td>
            <td>{transactionItem?.photos_no}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

CustomPrintTransactionDetails.propTypes = propTypes;

export default CustomPrintTransactionDetails;
